import ApiWithLogging from '../utils/apiWithLogging';
import { DeviceType, CreateDeviceTypeRequest, UpdateDeviceTypeRequest } from '../types/device';

// ต้องเพิ่มฟังก์ชันที่เข้ากันได้กับรูปแบบเดิม
// GetDeviceTypesParams interface
export interface GetDeviceTypesParams {
  page?: number;
  limit?: number;
  search?: string;
  is_active?: boolean;
}

// GetDeviceTypesResponse interface 
export interface GetDeviceTypesResponse {
  items: DeviceType[];
  total: number;
  page: number;
  limit: number;
}

const deviceTypeService = {
  // ดึงรายการประเภทอุปกรณ์ทั้งหมด (รูปแบบเดิม)
  async getDeviceTypes(params: GetDeviceTypesParams = {}): Promise<GetDeviceTypesResponse> {
    console.log('🔍 DeviceTypeService.getDeviceTypes called with params:', params);
    
    try {
      // ลองใช้ public endpoint ที่ไม่ต้อง auth ก่อน
      console.log('🌐 Trying public endpoint: /device-types/public/active');
      const response = await ApiWithLogging.get<GetDeviceTypesResponse>('/device-types/public/active', { 
        params: { limit: params.limit || 100 } 
      }, { service: 'DeviceType', skipLogging: true });
      console.log('✅ Public endpoint raw response:', response);
      
      // response จาก ApiWithLogging.get คือ response.data แล้ว
      if (response && typeof response === 'object') {
        console.log('✅ Returning response directly:', response);
        return response;
      } else {
        console.error('❌ Invalid response structure:', response);
        throw new Error('Invalid response structure from public endpoint');
      }
    } catch (error) {
      console.error('❌ Error fetching device types from public endpoint:', error);
      
      try {
        // ถ้า public endpoint ไม่ทำงาน ลอง endpoint เดิม
        console.log('🔄 Trying main endpoint: /device-types/');
        const response = await ApiWithLogging.get<GetDeviceTypesResponse>('/device-types/', { params }, { service: 'DeviceType', skipLogging: true });
        console.log('✅ Main endpoint response:', response); 
        return response;
      } catch (fallbackError) {
        console.error('❌ Both endpoints failed:', fallbackError);
        throw fallbackError;
      }
    }
  },

  // ดึงรายการประเภทอุปกรณ์ทั้งหมด
  async getAll(params?: {
    page?: number;
    limit?: number;
    search?: string;
    is_active?: boolean;
  }) {
    return await ApiWithLogging.get('/device-types/', { params }, { service: 'DeviceType', skipLogging: true });
  },

  // ดึงรายการประเภทอุปกรณ์ที่ใช้งานอยู่ (ไม่ต้อง auth)
  async getActive() {
    return await ApiWithLogging.get('/device-types/public/active', {}, { service: 'DeviceType', skipLogging: true });
  },

  // ดึงข้อมูลประเภทอุปกรณ์ตาม ID
  async getById(id: number) {
    return await ApiWithLogging.get(`/device-types/${id}`, {}, { service: 'DeviceType', skipLogging: true });
  },

  // สร้างประเภทอุปกรณ์ใหม่
  async create(data: CreateDeviceTypeRequest) {
    return await ApiWithLogging.post('/device-types/', data, {}, { service: 'DeviceType', skipLogging: true });
  },

  // อัพเดตประเภทอุปกรณ์
  // BUG-S162-P1: pass the record's last-seen updated_at as `ifMatch` to enable the
  // backend optimistic-concurrency lock (stale two-tab overwrite → 409). Optional.
  async update(id: number, data: UpdateDeviceTypeRequest, ifMatch?: string) {
    const config = ifMatch ? { headers: { 'If-Match': ifMatch } } : {};
    return await ApiWithLogging.put(`/device-types/${id}`, data, config, { service: 'DeviceType', skipLogging: true });
  },

  // ลบประเภทอุปกรณ์
  async delete(id: number) {
    return await ApiWithLogging.delete(`/device-types/${id}`, {}, { service: 'DeviceType', skipLogging: true });
  },

  // เปิด/ปิดการใช้งานประเภทอุปกรณ์ (สลับอัตโนมัติ)
  async toggleStatus(id: number) {
    return await ApiWithLogging.patch(`/device-types/${id}/toggle`, {}, {},
      { service: 'DeviceType', skipLogging: true });
  },

  // ส่งออกข้อมูลประเภทอุปกรณ์เป็น Excel
  // ส่งออก Excel — ต้องส่งตัวกรองที่หน้าจอใช้อยู่ไปด้วย ไม่งั้นผู้ใช้กรองแล้วกดส่งออก
  // จะได้ไฟล์ทั้งระบบโดยไม่มีอะไรเตือน (ไฟล์ที่ได้ต้องตรงกับที่เห็นบนจอ)
  async exportToExcel(params?: { search?: string; is_active?: boolean }): Promise<Blob> {
    const query: Record<string, string> = {};
    if (params?.search) query.search = params.search;
    if (params?.is_active !== undefined) query.is_active = String(params.is_active);

    const response = await ApiWithLogging.get('/device-types/export/excel',
      {
        responseType: 'blob',
        params: query
      },
      {
        service: 'deviceTypeService',
        action: 'export_device_types_excel',
        entityType: 'device_type'
      }
    );
    return response;
  }
};

// export ประเภท default เพื่อให้เข้ากันได้กับรูปแบบเดิม
export default deviceTypeService;